import { z } from 'zod'

import { wireDateTimeOptional } from '@/lib/api/schemas'
import type { AgentMcpBinding, McpTool } from '@/types/api'

function normalizeStringArray(raw: unknown): string[] | null {
  if (raw === null || raw === undefined) return null
  if (!Array.isArray(raw)) return null
  const names = raw.map((x) => String(x).trim()).filter(Boolean)
  return names
}

function parseToolsCacheRaw(raw: unknown): unknown[] | null | undefined {
  if (raw === null || raw === undefined) return raw
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return null
    try {
      const parsed: unknown = JSON.parse(trimmed)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return Array.isArray(raw) ? raw : []
}

export function normalizeMcpTool(raw: unknown): McpTool | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const name = String(o.name ?? '').trim()
  if (!name) return null
  const schemaRaw = o.inputSchema ?? o.input_schema
  return {
    name,
    description:
      o.description === null || o.description === undefined
        ? null
        : String(o.description),
    inputSchema:
      schemaRaw !== null && typeof schemaRaw === 'object'
        ? (schemaRaw as Record<string, unknown>)
        : {},
  }
}

/** Normaliza binding agent↔MCP (camelCase ou snake_case no wire). */
export function normalizeAgentMcpBinding(raw: unknown): AgentMcpBinding | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>

  const id = String(o.id ?? '').trim()
  const companyId = String(o.companyId ?? o.company_id ?? '').trim()
  const agentId = String(o.agentId ?? o.agent_id ?? '').trim()
  const mcpServerId = String(o.mcpServerId ?? o.mcp_server_id ?? '').trim()
  if (!id || !companyId || !agentId || !mcpServerId) return null

  const toolsCacheParsed = parseToolsCacheRaw(o.toolsCache ?? o.tools_cache)
  const toolsCache =
    toolsCacheParsed === null || toolsCacheParsed === undefined
      ? null
      : toolsCacheParsed
          .map(normalizeMcpTool)
          .filter((t): t is McpTool => t !== null)

  const allowedRaw = o.allowedTools ?? o.allowed_tools
  const allowedTools =
    allowedRaw === null || allowedRaw === undefined
      ? null
      : normalizeStringArray(allowedRaw) ?? []

  const lastHealthRaw = o.lastHealthAt ?? o.last_health_at
  const lastHealthAt =
    lastHealthRaw === null || lastHealthRaw === undefined
      ? null
      : String(lastHealthRaw)

  return {
    id,
    companyId,
    agentId,
    mcpServerId,
    fieldValuesJson:
      (o.fieldValuesJson ?? o.field_values_json) !== null &&
      typeof (o.fieldValuesJson ?? o.field_values_json) === 'object'
        ? ((o.fieldValuesJson ?? o.field_values_json) as Record<string, unknown>)
        : {},
    allowedTools,
    toolsCache,
    lastHealthAt,
    lastError:
      (o.lastError ?? o.last_error) === null || (o.lastError ?? o.last_error) === undefined
        ? null
        : String(o.lastError ?? o.last_error),
    isActive: Boolean(o.isActive ?? o.is_active ?? true),
    createdAt: String(o.createdAt ?? o.created_at ?? ''),
    updatedAt: String(o.updatedAt ?? o.updated_at ?? ''),
  }
}

export function normalizeAgentMcpBindingList(raw: unknown): AgentMcpBinding[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(normalizeAgentMcpBinding)
    .filter((b): b is AgentMcpBinding => b !== null)
}

const mcpToolSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable(),
  inputSchema: z.record(z.unknown()),
})

/** Schema Zod para validação de binding MCP no wire (Regra de Ouro #3 em datas). */
export const agentMcpBindingSchema = z.object({
  id: z.string().min(1),
  companyId: z.string().min(1),
  agentId: z.string().min(1),
  mcpServerId: z.string().min(1),
  fieldValuesJson: z.record(z.unknown()),
  allowedTools: z.array(z.string()).nullable(),
  toolsCache: z.array(mcpToolSchema).nullable(),
  lastHealthAt: wireDateTimeOptional,
  lastError: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: wireDateTimeOptional,
  updatedAt: wireDateTimeOptional,
})

export const agentMcpBindingListSchema = z.array(agentMcpBindingSchema)
